import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { AppLayout } from '../../components/layout/AppLayout';
import theme from '../../theme';

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
}

interface ImportStats {
  totalRecords: number;
  vehicleMatches: number;
  plateMatches: number;
  notFound: number;
  totalAmount: number;
  accuracy: number;
  duplicatesRemoved: number;
}

export const SalikImportScreen = () => {
  const navigation = useNavigation();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 6,
    message: 'Ready to import Salik data',
  });
  const [importStats, setImportStats] = useState<ImportStats | null>(null);

  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload CSV file from Salik system' },
    { id: 2, name: 'File Validation', description: 'Validate file format and structure' },
    { id: 3, name: 'Duplicate Detection', description: 'Identify and remove duplicate entries' },
    { id: 4, name: 'Vehicle Matching', description: 'Match vehicles using plate numbers' },
    { id: 5, name: 'Toll Processing', description: 'Process toll charges and gate information' },
    { id: 6, name: 'Database Update', description: 'Save toll data to accounting system' },
  ];

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: `Selected: ${result.assets[0].name}`,
        });
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick file');
      console.error('File picker error:', error);
    }
  };

  const simulateImport = async () => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a file first');
      return;
    }

    setProgress({ phase: 'uploading', currentStep: 1, totalSteps: 6, message: 'Uploading file...' });
    await new Promise(resolve => setTimeout(resolve, 1000));

    setProgress({ 
      phase: 'processing', 
      currentStep: 2, 
      totalSteps: 6, 
      message: 'Validating file format...',
      details: 'Checking CSV structure and Salik headers'
    });
    await new Promise(resolve => setTimeout(resolve, 800));

    setProgress({ 
      phase: 'processing', 
      currentStep: 3, 
      totalSteps: 6, 
      message: 'Detecting duplicates...',
      details: 'Scanning for duplicate toll entries'
    });
    await new Promise(resolve => setTimeout(resolve, 1500));

    setProgress({ 
      phase: 'processing', 
      currentStep: 4, 
      totalSteps: 6, 
      message: 'Matching vehicles using plate numbers...',
      details: 'Comparing plate numbers with vehicle database'
    });
    
    // Simulate progressive matching
    for (let i = 0; i <= 100; i += 8) {
      setProgress({ 
        phase: 'processing', 
        currentStep: 4, 
        totalSteps: 6, 
        message: 'Matching vehicles using plate numbers...',
        details: `Processed ${i}% of records`,
        processed: i * 8.91, // Simulate 891 total records
        total: 891
      });
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setProgress({ 
      phase: 'processing', 
      currentStep: 5, 
      totalSteps: 6, 
      message: 'Processing toll charges...',
      details: 'Analyzing gate crossings and calculating charges'
    });
    await new Promise(resolve => setTimeout(resolve, 1200));

    setProgress({ 
      phase: 'processing', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Updating database...',
      details: 'Saving toll transactions to accounting system'
    });
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Set final results
    const stats: ImportStats = {
      totalRecords: 891,
      vehicleMatches: 834,
      plateMatches: 41,
      notFound: 16,
      totalAmount: 12847,
      accuracy: 98.2,
      duplicatesRemoved: 23,
    };

    setImportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Import completed successfully!',
      details: `${stats.vehicleMatches + stats.plateMatches} toll transactions imported`
    });
  };

  const resetImport = () => {
    setSelectedFile(null);
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 6,
      message: 'Ready to import Salik data',
    });
    setImportStats(null);
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#FF6B35'; // Salik orange
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#FF6B35" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Salik Import" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#FF6B35' + '15' }]}>
              <Ionicons name="radio" size={24} color="#FF6B35" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Salik Toll Import</Text>
              <Text style={styles.subtitle}>Import toll gate charges and vehicle crossings from Salik CSV</Text>
            </View>
          </View>

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Select File</Text>
            <TouchableOpacity style={[styles.filePickerButton, { borderColor: '#FF6B35' }]} onPress={pickFile}>
              <Ionicons name="cloud-upload" size={20} color="#FF6B35" />
              <Text style={[styles.filePickerText, { color: '#FF6B35' }]}>
                {selectedFile ? selectedFile.name : 'Choose Salik CSV file'}
              </Text>
            </TouchableOpacity>
            {selectedFile && (
              <View style={styles.fileInfo}>
                <Ionicons name="document-text" size={16} color={theme.colors.success} />
                <Text style={styles.fileInfoText}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </View>
            )}
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Import Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#FF6B35' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {progress.phase === 'processing' && (
                    <ActivityIndicator size="small" color="#FF6B35" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressBar}>
                    <View 
                      style={[
                        styles.progressFill, 
                        { 
                          width: `${(progress.processed / progress.total) * 100}%`,
                          backgroundColor: '#FF6B35'
                        }
                      ]} 
                    />
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Import Statistics */}
          {importStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>3. Import Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{importStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#FF6B35' }]}>
                    {importStats.vehicleMatches}
                  </Text>
                  <Text style={styles.statLabel}>Vehicle Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.warning }]}>
                    {importStats.plateMatches}
                  </Text>
                  <Text style={styles.statLabel}>Plate Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                    {importStats.notFound}
                  </Text>
                  <Text style={styles.statLabel}>Not Found</Text>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Amount:</Text>
                  <Text style={styles.summaryValue}>
                    AED {importStats.totalAmount.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Accuracy:</Text>
                  <Text style={[styles.summaryValue, { color: '#FF6B35' }]}>
                    {importStats.accuracy}%
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Duplicates Removed:</Text>
                  <Text style={styles.summaryValue}>
                    {importStats.duplicatesRemoved}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Import Date:</Text>
                  <Text style={styles.summaryValue}>
                    {new Date().toLocaleDateString()}
                  </Text>
                </View>
              </View>

              {/* Salik-specific insights */}
              <View style={styles.insightCard}>
                <View style={styles.insightHeader}>
                  <Ionicons name="analytics" size={20} color="#FF6B35" />
                  <Text style={styles.insightTitle}>Salik Insights</Text>
                </View>
                <Text style={styles.insightText}>
                  • Vehicle plate matching achieved {((importStats.vehicleMatches / importStats.totalRecords) * 100).toFixed(1)}% precision
                </Text>
                <Text style={styles.insightText}>
                  • {importStats.duplicatesRemoved} duplicate toll entries were automatically removed
                </Text>
                <Text style={styles.insightText}>
                  • Only {importStats.notFound} vehicle(s) require manual assignment
                </Text>
                <Text style={styles.insightText}>
                  • Toll data synchronized with fleet expense tracking
                </Text>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {progress.phase === 'idle' || progress.phase === 'complete' ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#FF6B35' },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={simulateImport}
                  disabled={!selectedFile || progress.phase === 'processing'}
                >
                  <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    {progress.phase === 'complete' ? 'Import Another File' : 'Start Import'}
                  </Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#FF6B35' }]} 
                    onPress={resetImport}
                  >
                    <Ionicons name="refresh" size={20} color="#FF6B35" />
                    <Text style={[styles.secondaryButtonText, { color: '#FF6B35' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <TouchableOpacity style={styles.disabledButton} disabled>
                <ActivityIndicator size="small" color={theme.colors.white} />
                <Text style={styles.primaryButtonText}>Processing...</Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#FF6B35' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: theme.colors.gray200,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  insightCard: {
    padding: theme.spacing.lg,
    backgroundColor: '#FF6B35' + '10',
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: '#FF6B35' + '20',
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  insightTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: '#FF6B35',
  },
  insightText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
    lineHeight: 18,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
});
